import { Switch } from "@/components/ui/switch";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { DataTableColumnHeader } from "./components/data-table-column-header";
import { RowActions } from "./components/row-actions";
import { ShippingTypeBadges } from "./components/shipping-type-badges";
import { COLUMN_TITLES, SECTOR_LABELS } from "./constants";
import { RecipientActions } from "./hooks/use-recipients-source";
import { SECTOR_EMAILS_LABELS } from "./messages";
import { SectorRecipient } from "./types";

interface UseRecipientColumnsParams {
  actions: RecipientActions;
}

export const useRecipientColumns = ({
  actions,
}: UseRecipientColumnsParams): ColumnDef<SectorRecipient>[] =>
  useMemo(
    (): ColumnDef<SectorRecipient>[] => [
      {
        id: "active",
        accessorKey: "active",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={COLUMN_TITLES.ACTIVE} />
        ),
        cell: ({ row }) => (
          <Switch
            checked={row.original.active}
            onCheckedChange={() => actions.toggleActive(row.original)}
            title={
              row.original.active
                ? SECTOR_EMAILS_LABELS.ACTIVE_HINT
                : SECTOR_EMAILS_LABELS.INACTIVE_HINT
            }
            aria-label={
              row.original.active
                ? SECTOR_EMAILS_LABELS.DEACTIVATE
                : SECTOR_EMAILS_LABELS.REACTIVATE
            }
          />
        ),
      },
      {
        id: "sector",
        accessorFn: (recipient: SectorRecipient): string =>
          SECTOR_LABELS[recipient.sector],
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={COLUMN_TITLES.SECTOR} />
        ),
        cell: ({ row }) => (
          <span className="font-medium text-gray-900">
            {SECTOR_LABELS[row.original.sector]}
          </span>
        ),
      },
      {
        id: "recipient",
        accessorFn: (recipient: SectorRecipient): string =>
          recipient.name || recipient.email,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={COLUMN_TITLES.RECIPIENT}
          />
        ),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">
              {row.original.name || "—"}
            </span>
            <span className="text-xs text-gray-500">{row.original.email}</span>
          </div>
        ),
      },
      {
        id: "shipping_types",
        header: COLUMN_TITLES.SHIPPING_TYPES,
        cell: ({ row }) => (
          <ShippingTypeBadges types={row.original.shipping_types} />
        ),
        enableSorting: false,
      },
      {
        id: "actions",
        header: COLUMN_TITLES.ACTIONS,
        cell: ({ row }) => (
          <RowActions recipient={row.original} actions={actions} />
        ),
        enableSorting: false,
      },
    ],
    [actions]
  );
